import React, { createContext, useContext, useState, useCallback, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import { FiAlertTriangle, FiHelpCircle } from "react-icons/fi";
import { shakeModal } from "../utils/modalAttention";
import s from "./ConfirmDialog.module.css";

/**
 * Globalny, ładny dialog potwierdzenia — zamiennik natywnego window.confirm().
 * Użycie:
 *   const confirm = useConfirm();
 *   if (!(await confirm({ message: "Na pewno usunąć?", danger: true, confirmText: "Usuń" }))) return;
 *
 * Zwraca Promise<boolean>. Mbuilt na portalu, więc działa z dowolnego komponentu funkcyjnego.
 *
 * [poprawka NOWE-ZGLOSZENIE 2026-09-28] KLAWIATURA JAK W DIALOGU WAI-ARIA (alertdialog). Pytanie stało dotąd POZA
 * stosem okien `Modal`, więc pod spodem działała dalej pułapka Tab okna gospodarza (Tab uciekał pod nakładkę),
 * a Escape trafiał do okna pod spodem i nic nie zamykał — z klawiatury nie dało się wybrać „Wróć do wniosku”.
 * Teraz, dopóki pytanie jest otwarte: Escape = odpowiedź „nie” (przycisk anulowania), Tab/Shift+Tab krąży po
 * przyciskach pytania, a oba klawisze są zatrzymywane w fazie przechwytywania okna — Modal ani szuflada pod spodem
 * ich nie dostają. Po odpowiedzi fokus wraca tam, skąd pytanie przyszło. Opcja `fokusNaAnulowaniu` stawia fokus
 * startowy na przycisku anulowania (pytanie o porzucenie danych: odruchowy Enter nie może ich skasować).
 */
const ConfirmContext = createContext(null);

export const ConfirmDialogProvider = ({ children }) => {
    const [state, setState] = useState(null);
    const resolverRef = useRef(null);
    const dialogRef = useRef(null);
    const poprzedniFokusRef = useRef(null);

    const confirm = useCallback((opts = {}) => {
        const options = typeof opts === "string" ? { message: opts } : opts;
        // Element z fokusem SPRZED pytania — tam wraca fokus po odpowiedzi (drugie pytanie nie nadpisuje pierwszego źródła).
        if (!resolverRef.current && typeof document !== "undefined") poprzedniFokusRef.current = document.activeElement;
        return new Promise((resolve) => {
            resolverRef.current = resolve;
            setState({
                title: options.title || "Potwierdzenie",
                message: options.message || "Czy na pewno chcesz kontynuować?",
                confirmText: options.confirmText || "Potwierdź",
                cancelText: options.cancelText || "Anuluj",
                danger: options.danger ?? false,
                fokusNaAnulowaniu: !!options.fokusNaAnulowaniu,
            });
        });
    }, []);

    const close = useCallback((result) => {
        setState(null);
        const poprzedni = poprzedniFokusRef.current;
        poprzedniFokusRef.current = null;
        if (poprzedni && typeof poprzedni.focus === "function" && typeof document !== "undefined" && document.contains(poprzedni)) {
            try { poprzedni.focus(); } catch { /* element właśnie znika — fokus zostaje tam, gdzie jest */ }
        }
        if (resolverRef.current) {
            resolverRef.current(result);
            resolverRef.current = null;
        }
    }, []);

    /* Klawiatura pytania: Escape = „nie”, Tab krąży po jego przyciskach. Nasłuch w fazie PRZECHWYTYWANIA na `window`
       i `stopPropagation` — okno (Modal) i szuflada pod spodem słuchają na `document`, więc tych klawiszy nie dostaną. */
    useEffect(() => {
        if (!state) return undefined;
        const naKlawisz = (e) => {
            if (e.key === "Escape") {
                e.preventDefault();
                e.stopPropagation();
                close(false);
                return;
            }
            if (e.key !== "Tab") return;
            const dialog = dialogRef.current;
            if (!dialog) return;
            e.preventDefault();
            e.stopPropagation();
            const lista = Array.from(dialog.querySelectorAll("button:not([disabled])"));
            if (lista.length === 0) return;
            const i = lista.indexOf(document.activeElement);
            const j = e.shiftKey
                ? (i <= 0 ? lista.length - 1 : i - 1)
                : (i < 0 || i === lista.length - 1 ? 0 : i + 1);
            lista[j].focus();
        };
        window.addEventListener("keydown", naKlawisz, true);
        return () => window.removeEventListener("keydown", naKlawisz, true);
    }, [state, close]);

    return (
        <ConfirmContext.Provider value={confirm}>
            {children}
            {state && createPortal(
                <div className={s.overlay} onClick={shakeModal}>
                    <div
                        ref={dialogRef}
                        className={s.dialog}
                        onClick={(e) => e.stopPropagation()}
                        role="alertdialog"
                        aria-modal="true"
                        aria-label={state.title}
                    >
                        <div className={`${s.iconWrap} ${state.danger ? s.iconDanger : s.iconInfo}`}>
                            {state.danger ? <FiAlertTriangle size={22} /> : <FiHelpCircle size={22} />}
                        </div>
                        <h3 className={s.title}>{state.title}</h3>
                        <p className={s.message}>{state.message}</p>
                        <div className={s.actions}>
                            <button
                                type="button"
                                className={s.cancelBtn}
                                onClick={() => close(false)}
                                autoFocus={state.fokusNaAnulowaniu}
                            >
                                {state.cancelText}
                            </button>
                            <button
                                type="button"
                                className={`${s.confirmBtn} ${state.danger ? s.confirmDanger : ""}`}
                                onClick={() => close(true)}
                                autoFocus={!state.fokusNaAnulowaniu}
                            >
                                {state.confirmText}
                            </button>
                        </div>
                    </div>
                </div>,
                document.body
            )}
        </ConfirmContext.Provider>
    );
};

export const useConfirm = () => {
    const ctx = useContext(ConfirmContext);
    if (!ctx) throw new Error("useConfirm must be used within ConfirmDialogProvider");
    return ctx;
};

/**
 * 🔴 WARIANT BEZ WYJĄTKU — zwraca `null`, gdy providera nad komponentem NIE MA.
 *
 * Po co: `useConfirm` RZUCA, a to znaczy BIAŁY EKRAN całego widoku, nie jednej nieczynnej akcji.
 * Ekrany, które montują się w wielu otoczeniach (karta zgłoszenia otwiera się z ~20 miejsc, kolejka
 * bywa osadzana w landingach modułów), nie mogą się wywracać przez brakujący provider — repozytorium
 * płaciło już za to dwa razy i dlatego `KrokiDrawer` montuje się dopiero po kliknięciu, a
 * `SekcjaPoufnosciSprawy` ma własne potwierdzenie w wierszu.
 *
 * Reguła użycia: `null` = akcji wymagającej potwierdzenia NIE POKAZUJEMY W OGÓLE. Nigdy nie wolno
 * wykonać jej „bez pytania" — brak okna nie może znaczyć braku zgody.
 */
export const useConfirmOpcjonalny = () => useContext(ConfirmContext);
